import { InjectionKey, ref } from 'vue';
import { COMMON_IMG_URL } from '@/constants/BlackList';
import { Bancolle } from '@/modules/Collection';
import { MylistItem } from '@/modules/MylistItem';
import { _Program } from '@/modules/Program';
import { SearchTargetProgram } from '@/modules/SearchedProgram';
import { BlacklistMap } from '@/repositories/blackProgramsRepository';

export const DisplayType = {
  ReplaceImg: '0',
  Hidden: '1',
} as const;

export const ContentType = {
  Program: '0',
  Bancolle: '1',
  Present: '2',
} as const;

export const useBlacklist = () => {
  const blacklist = ref<BlacklistMap>(new Map());

  const setBlacklist = (setData: BlacklistMap) => {
    blacklist.value = setData;
  };

  /**
   * 非表示ブラックリストに該当する番組を非表示か否か判断する
   */
  const isHiddenProgram = (id: string): boolean => {
    const target = blacklist.value.get(id);

    //対象が「番組」or「番コレ」で、制御モードが「非表示」の場合：true
    return target?.contentType !== ContentType.Present && target?.displayType === DisplayType.Hidden ? true : false;
  };

  /**
   * 番組のサムネ画像を条件に応じて出し分ける関数
   */
  const getProgramThumbnail = (id: string, futurePrograms: _Program[] | null, originalThumbnail: string): string => {
    const { value, isReplaced } = _getThumbnail(id, originalThumbnail);
    // 画像が差し替えられていたら返す
    if (isReplaced) return value;

    return !futurePrograms?.length ? COMMON_IMG_URL : value;
  };

  /**
   * プレゼントのサムネ画像を出し分ける関数
   */
  const getPresentThumbnail = (id: string, originalThumbnail: string): string => {
    const { value } = _getThumbnail(id, originalThumbnail);

    return value;
  };

  /**
   * IDがブラックリストに該当していた場合、差し替え画像を返す関数
   */
  const _getThumbnail = (id: string, originalThumbnail: string) => {
    const target = blacklist.value.get(id);

    const value =
      target?.contentType === ContentType.Present || target?.displayType === DisplayType.ReplaceImg
        ? target.url || COMMON_IMG_URL
        : originalThumbnail;
    const isReplaced = value !== originalThumbnail;

    return { value, isReplaced };
  };

  // 入力された型を判定して返す型定義
  type filterTargetOption<T extends Bancolle[] | MylistItem[] | SearchTargetProgram[]> = T extends Bancolle[]
    ? Bancolle[]
    : T extends MylistItem[]
    ? MylistItem[]
    : SearchTargetProgram[];

  // 配列から非表示ブラックリストに該当するものを弾く
  const excludeBlackList = <T extends Bancolle[] | MylistItem[] | SearchTargetProgram[]>(
    items: T
  ): filterTargetOption<T> => {
    const isBancolle = (arg: any): arg is Bancolle[] => arg[0]?.id;
    const isMylistItem = (arg: any): arg is MylistItem[] => arg[0]?.channelSignatureName;

    if (isBancolle(items)) {
      return <filterTargetOption<T>>items.filter((item) => {
        return !isHiddenProgram(item.id);
      });
    } else if (isMylistItem(items)) {
      return <filterTargetOption<T>>items.filter((item) => {
        return !isHiddenProgram(item.channel.channelSignatureName);
      });
    } else {
      return <filterTargetOption<T>>items.filter((item) => {
        return !isHiddenProgram(item.id);
      });
    }
  };

  return { isHiddenProgram, setBlacklist, blacklist, excludeBlackList, getPresentThumbnail, getProgramThumbnail };
};

export type BlackListType = ReturnType<typeof useBlacklist>;
export const BlackListKey: InjectionKey<BlackListType> = Symbol('BlackList');
